import React, { useState, useEffect } from 'react';
import { apiKeysApi } from '../utils/api';
import Layout from '../components/Layout';

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({ name: '', expiresIn: '' });

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await apiKeysApi.list();
      setApiKeys(response.data.apiKeys);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await apiKeysApi.create({
        name: formData.name,
        expiresIn: formData.expiresIn || undefined,
      });
      setNewKey(response.data.apiKey);
      setFormData({ name: '', expiresIn: '' });
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to create API key:', error);
      alert(error.response?.data?.error || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const handleRotate = async (id) => {
    if (!window.confirm('Are you sure? The old key will stop working immediately.')) return;
    try {
      const response = await apiKeysApi.rotate(id);
      setNewKey(response.data.apiKey);
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to rotate API key:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this API key?')) return;
    try {
      await apiKeysApi.delete(id);
      fetchApiKeys();
    } catch (error) {
      console.error('Failed to delete API key:', error);
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    alert('API key copied to clipboard!');
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Layout>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="page-header">API Keys</h1>
          <p className="text-gray-600 mt-1">Manage API keys for programmatic access</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary mt-4 sm:mt-0">
          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Create API Key
        </button>
      </div>

      {/* API Documentation */}
      <div className="card p-6 mb-8 bg-gray-50 border-gray-200">
        <h3 className="font-medium text-gray-900 mb-2">Using the API</h3>
        <p className="text-sm text-gray-600 mb-3">
          Include your API key in the <code className="bg-gray-200 px-1 rounded">X-API-Key</code> header:
        </p>
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`curl -X GET "http://localhost:3001/api/projects" \\
  -H "X-API-Key: oe_xxxx_your_api_key_here"`}
        </pre>
      </div>

      {/* New Key Display */}
      {newKey && (
        <div className="card p-6 mb-8 bg-green-50 border-green-200">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-green-800 mb-1">API Key Created: {newKey.name}</h3>
              <p className="text-sm text-green-700 mb-3">
                Copy this key now — it will not be shown again.
              </p>
              <div className="flex items-center gap-2">
                <code className="bg-white px-3 py-2 rounded border border-green-200 text-sm font-mono">
                  {newKey.key}
                </code>
                <button onClick={() => copyKey(newKey.key)} className="btn-primary text-sm py-1.5">
                  Copy
                </button>
              </div>
            </div>
            <button onClick={() => setNewKey(null)} className="text-green-600 hover:text-green-700">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* API Keys List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      ) : apiKeys.length > 0 ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Key Prefix</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Last Used</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase">Expires</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {apiKeys.map((key) => (
                <tr key={key.id} className={!key.is_active ? 'opacity-50' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-gray-900">{key.name}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">{key.key_prefix}_••••••</code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      key.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {key.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(key.last_used_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {key.expires_at ? formatDate(key.expires_at) : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    {key.is_active && (
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleRotate(key.id)} 
                          className="text-sm text-primary-600 hover:text-primary-700">
                          Rotate
                        </button>
                        <button onClick={() => handleDelete(key.id)}
                          className="text-sm text-red-600 hover:text-red-700">
                          Revoke
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No API keys</h3>
          <p className="mt-2 text-gray-600">Create an API key to enable programmatic access.</p>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" 
          onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Create API Key</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label htmlFor="keyName" className="label">Name</label>
                <input id="keyName" type="text" required className="input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Production API" />
              </div>
              <div>
                <label htmlFor="expiresIn" className="label">Expires (optional)</label>
                <select id="expiresIn" className="input"
                  value={formData.expiresIn}
                  onChange={(e) => setFormData({ ...formData, expiresIn: e.target.value })}>
                  <option value="">Never</option>
                  <option value="7d">7 days</option>
                  <option value="30d">30 days</option>
                  <option value="90d">90 days</option>
                  <option value="365d">1 year</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="btn-primary">
                  {creating ? 'Creating...' : 'Create Key'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
